import React from 'react';
import { tp, useP } from '../../../i18n/useP';
import { useTranslation } from 'react-i18next';
import { MOIS_COURT } from '../engine/constants';
import './OpeningPositionsStep.css';

// Point de départ → étape « Postes ouverts ». Liste libre de positions ouvertes
// au démarrage du plan. Pour chaque poste : une « Valeur ouverte » (créance = +,
// dette = −) et un profil d'utilisation en POURCENTAGES sur les 6 premiers mois
// (M1-M6) : quelle part de la valeur est encaissée / décaissée chaque mois.
// État contrôlé (remonté dans TreasuryPage → moteur cash). Persistance par
// scénario côté parent.
const NPCT = 6;   // nombre de mois d'étalement (M1..M6)
const zeros = () => Array(NPCT).fill(0);

export const OPENING_DEF = [
  { label: tp('phrases:p_6e20fbea0b'),        type: 'in',  value: 0, pct: zeros() },
  { label: tp('phrases:p_573a205416'),     type: 'out', value: 0, pct: zeros() },
  { label: tp('phrases:p_b159d3bdbb'), type: 'out', value: 0, pct: zeros() },
];

const num = (v) => Number(v) || 0;
// Profil de pourcentages d'une ligne, tolérant à l'ancien format (m absolu → 0).
const pctOf = (r) => {
  const src = Array.isArray(r?.pct) ? r.pct : [];
  return Array.from({ length: NPCT }, (_, k) => num(src[k]));
};

// Convertit les postes ouverts manuels en lignes créances/dettes consommées par
// le moteur cash : une ligne par poste, avec la valeur ouverte (montant) et un
// profil de collection/paiement en % sur 12 mois (les % M1..M6 sont placés aux
// mois calendaires correspondants ; le reste à 0).
export function openingToLines(rows, startMonth) {
  const list = Array.isArray(rows) && rows.length ? rows : OPENING_DEF;
  const receivables = [];
  const payables = [];
  list.forEach((r, idx) => {
    const amt = Math.abs(num(r.value));
    if (amt === 0) return;
    const prof = Array(12).fill(0);
    pctOf(r).forEach((p, k) => { prof[((startMonth || 0) + k) % 12] = num(p); });
    if (r.type === 'out') payables.push({ id: `man-${idx}`, supplier_name: r.label, amount: amt, payment_pct: prof });
    else receivables.push({ id: `man-${idx}`, customer_name: r.label, amount: amt, collection_pct: prof });
  });
  return { receivables, payables };
}

const parse = (s) => {
  const raw = String(s).replace(/[^0-9.,-]/g, '').replace(',', '.');
  return raw === '' ? 0 : Number(raw);
};
// Signe imposé par le type : encaissement ≥ 0, décaissement ≤ 0.
const signed = (v, type) => (type === 'out' ? -Math.abs(num(v)) : Math.abs(num(v)));
// Pourcentage borné [0 ; 100] (part d'utilisation, sans signe).
const clampPct = (v) => Math.max(0, Math.min(100, num(v)));

// Cellule montant éditable (bleu, sans cadre), signe forcé au commit.
function Amount({ value, onCommit, disabled }) {
  const [draft, setDraft] = React.useState(null);
  const display = draft !== null ? draft : (num(value) ? String(num(value)) : '');
  return (
    <input
      className="opos-amt" type="text" inputMode="decimal" disabled={disabled}
      value={display} placeholder="0"
      onFocus={() => setDraft(String(num(value) || ''))}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { if (draft !== null) onCommit(parse(draft)); setDraft(null); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
    />
  );
}

// Cellule pourcentage éditable (0-100), suffixe %.
function Pct({ value, onCommit, disabled }) {
  const [draft, setDraft] = React.useState(null);
  const display = draft !== null ? draft : (num(value) ? String(num(value)) : '');
  return (
    <span className="opos-pctcell">
      <input
        className="opos-amt opos-pct" type="text" inputMode="decimal" disabled={disabled}
        value={display} placeholder="0"
        onFocus={() => setDraft(String(num(value) || ''))}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { if (draft !== null) onCommit(clampPct(parse(draft))); setDraft(null); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      />
      <span className="opos-pctsign">%</span>
    </span>
  );
}

export default function OpeningPositionsStep({ scenario, locked, rows, onChange }) {
  const { t } = useTranslation();
  const p = useP();
  const setRows = onChange;
  const list = Array.isArray(rows) && rows.length ? rows : OPENING_DEF;

  // Libellés M1-M6 = 6 premiers mois de la fenêtre.
  const start = scenario?.start_month ?? 0;
  const mLabel = (k) => `M${k + 1} · ${MOIS_COURT[(start + k) % 12]}`;

  const setRow = (idx, patch) => setRows(list.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const setType = (idx, type) => setRow(idx, { type, value: signed(list[idx].value, type) });
  const setValue = (idx, raw) => setRow(idx, { value: signed(raw, list[idx].type) });
  const setPct = (idx, k, raw) => {
    const pct = pctOf(list[idx]); pct[k] = clampPct(raw); setRow(idx, { pct });
  };
  const addRow = () => setRows([...list, { label: p('Nouvelle ligne'), type: 'in', value: 0, pct: zeros() }]);
  const delRow = (idx) => setRows(list.filter((_, i) => i !== idx));

  return (
    <div className="opos">
      <div className="opos-hint">
        {p('Positions ouvertes au démarrage. Valeur ouverte (créances = positif, dettes = négatif), puis part encaissée / décaissée en % sur M1-M6.')}
      </div>
      <div className="opos-wrap">
        <table className="opos-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{t('phrases:p_f5485bbae8')}</th>
              <th>{p('Type')}</th>
              <th>{p('Valeur ouverte')}</th>
              {Array.from({ length: NPCT }, (_, k) => <th key={k}>{mLabel(k)}</th>)}
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((r, idx) => {
              const pct = pctOf(r);
              return (
                <tr key={idx}>
                  <td style={{ textAlign: 'left' }}>
                    <input className="opos-label" type="text" disabled={locked} value={r.label}
                      onChange={(e) => setRow(idx, { label: e.target.value })} />
                  </td>
                  <td>
                    <select className={`opos-type opos-type--${r.type}`} disabled={locked} value={r.type}
                      onChange={(e) => setType(idx, e.target.value)}>
                      <option value="in">{p('Encaissement')}</option>
                      <option value="out">{p('Décaissement')}</option>
                    </select>
                  </td>
                  <td><Amount value={r.value} disabled={locked} onCommit={(v) => setValue(idx, v)} /></td>
                  {Array.from({ length: NPCT }, (_, k) => (
                    <td key={k}><Pct value={pct[k]} disabled={locked} onCommit={(v) => setPct(idx, k, v)} /></td>
                  ))}
                  <td>
                    {!locked && (
                      <button type="button" className="opos-del" title={p('Supprimer la ligne')}
                        onClick={() => delRow(idx)}>×</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!locked && <button type="button" className="opos-add" onClick={addRow}>{p('+ Ajouter une ligne')}</button>}
    </div>
  );
}
